/**
 * Enhanced localStorage utility functions
 * Provides methods for getting, setting, and removing items from localStorage
 * with additional functionality to notify components of changes
 */

/**
 * Custom event for localStorage changes
 */
const STORAGE_EVENT = 'app_storage_change';

/**
 * Get item from localStorage and parse it as JSON
 * @param {string} key - The key to retrieve 
 * @param {any} defaultValue - Default value if key doesn't exist
 * @returns {any} - The parsed value or defaultValue
 */
export const getLocalStorageItem = (key, defaultValue = null) => {
  try {
    const item = localStorage.getItem(key);
    return item ? JSON.parse(item) : defaultValue;
  } catch (error) {
    console.error(`Error getting localStorage item ${key}:`, error);
    return defaultValue;
  }
};

/**
 * Set item in localStorage with JSON stringification and dispatch a custom event
 * @param {string} key - The key to set
 * @param {any} value - The value to store (will be JSON stringified)
 */
export const setLocalStorageItem = (key, value) => {
  try {
    const oldValue = localStorage.getItem(key);
    
    // Store the value in localStorage
    if (value === null || value === undefined) {
      localStorage.removeItem(key);
    } else {
      localStorage.setItem(key, JSON.stringify(value));
    }
    
    // Dispatch a custom event to notify listeners
    const event = new CustomEvent(STORAGE_EVENT, {
      detail: {
        key,
        newValue: value ? JSON.stringify(value) : null,
        oldValue,
      }
    });
    window.dispatchEvent(event);
    
    // Also dispatch a storage event for compatibility
    // This helps with cross-tab communication
    if (window.dispatchEvent) {
      const storageEvent = new StorageEvent('storage', {
        key,
        newValue: value ? JSON.stringify(value) : null,
        oldValue,
        storageArea: localStorage,
      });
      window.dispatchEvent(storageEvent);
    }
  } catch (error) {
    console.error(`Error setting localStorage item ${key}:`, error);
  }
};

/**
 * Remove item from localStorage and dispatch a custom event
 * @param {string} key - The key to remove
 */
export const removeLocalStorageItem = (key) => {
  try {
    const oldValue = localStorage.getItem(key);
    
    // Remove the item
    localStorage.removeItem(key);
    
    // Dispatch a custom event to notify listeners
    const event = new CustomEvent(STORAGE_EVENT, {
      detail: {
        key,
        newValue: null,
        oldValue,
      }
    });
    window.dispatchEvent(event);
    
    // Also dispatch a storage event for compatibility
    if (window.dispatchEvent) {
      const storageEvent = new StorageEvent('storage', {
        key,
        newValue: null,
        oldValue,
        storageArea: localStorage,
      });
      window.dispatchEvent(storageEvent);
    }
  } catch (error) {
    console.error(`Error removing localStorage item ${key}:`, error);
  }
};

/**
 * Clear all localStorage items and dispatch a custom event
 */
export const clearLocalStorage = () => {
  try {
    localStorage.clear();
    
    // Dispatch a custom event to notify listeners
    const event = new CustomEvent(STORAGE_EVENT, {
      detail: {
        key: null,
        newValue: null,
        oldValue: null,
        cleared: true
      }
    });
    window.dispatchEvent(event);
  } catch (error) {
    console.error('Error clearing localStorage:', error);
  }
};

/**
 * Add a listener for localStorage changes
 * @param {Function} callback - Function to call when localStorage changes
 * @returns {Function} - Function to remove the listener
 */
export const addLocalStorageListener = (callback) => {
  const handleStorageChange = (event) => {
    // Custom event
    if (event.type === STORAGE_EVENT) {
      callback(event.detail);
    }
    // Standard storage event
    else if (event.type === 'storage') {
      callback({
        key: event.key,
        newValue: event.newValue,
        oldValue: event.oldValue,
      });
    }
  };
  
  window.addEventListener(STORAGE_EVENT, handleStorageChange);
  window.addEventListener('storage', handleStorageChange);
  
  // Return a function to remove both listeners
  return () => {
    window.removeEventListener(STORAGE_EVENT, handleStorageChange);
    window.removeEventListener('storage', handleStorageChange);
  };
};
